import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../../services/mainApi.service";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Chip } from "@mui/material";
import { ILists } from "../../../../models/lists";
import { useCallback } from "react";
import ContactsIcon from "@mui/icons-material/Contacts";

const BANK_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/List/";
const bankService = new MainApiService<ILists>(BANK_ROUTE);

const translationPath = "page.4uDlist.list.";

const formatCreated = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy HH:mm");
};

const joinNames = (items?: { name?: string | null }[] | null) =>
  (items ?? [])
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");

const ListsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns: GridColDef<ILists>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "description",
      headerName: t(translationPath + "description") || "Description",
      renderCell: ({ row: { description } }) => {
        return description && description.length > 0 ? (
          <OverflowTooltip
            text={description}
            tooltip={description}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created_date") || "Created At",
      maxWidth: 170,
      renderCell: ({ row: { created } }) => formatCreated(created),
    },
    {
      field: "listCategories",
      headerName: t(translationPath + "category") || "Category",
      sortable: false,
      renderCell: ({ row: { listCategories } }) => {
        const names = joinNames(listCategories);
        return names ? (
          <OverflowTooltip
            text={names}
            tooltip={names}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "listClassifications",
      headerName: t(translationPath + "classification") || "Classification",
      sortable: false,
      renderCell: ({ row: { listClassifications } }) => {
        const names = joinNames(listClassifications);
        return names ? (
          <OverflowTooltip
            text={names}
            tooltip={names}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 150,
      renderCell: ({ row: { enable } }) => {
        return (
          <Chip
            label={
              enable
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={enable ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleRowClick = useCallback((params: GridRowParams<ILists>) => {
    navigate(`${params.id.toString()}/edit`);
  }, []);

  const handleOpenListContacts = useCallback((id: string) => {
    navigate(`listContacts/${id}`);
  }, []);

  const handleAdd = useCallback(() => {
    navigate("new");
  }, []);

  return (
    <Table<ILists>
      showDefaultMacroFilters={false}
      service={bankService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      rowActions={(params) => {
        if (params.row.id) {
          return [
            {
              action: () => handleOpenListContacts(params.row.id),
              icon: <ContactsIcon fontSize="small" />,
              text: t(translationPath + "list_contacts"),
            },
          ];
        }

        return [];
      }}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a list classification"
      }
      addButtonLabel={t(translationPath + "add") || "Add List Classification"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ListsPage;
